import React from "react";

import Box from "@busbud/design-system-components/dist/Box";
import Stack from "@busbud/design-system-components/dist/Stack";
import Text from "@busbud/design-system-components/dist/Text";

import { Markdown } from "@app/components/markdown";
import type { ErrorModalButtonType } from "@app/helpers/api-error";
import { useAppContext } from "@app/helpers/hooks";
import { hasRefundProtectionPurchased } from "@app/modules/account/components/trip-details/helpers";
import { HepstarBannerChip } from "@app/modules/account/components/trip-details/hepstar-banner/hepstar-banner";
import { useModalActions } from "@app/store/hooks/useModalActions";
import type { NapiPurchaseCancellation } from "@app/types/napi-v2-cancellation-types";

import type { RefundFormState } from "../cancel-trip";

import {
  getButtons,
  getCtaAction,
  getHepstarBannerProps,
  getIcon,
  getMessage,
  getTitle
} from "./prop-helpers";
import { CancelDialog } from "../cancel-dialog";
import { useCancelTripContext } from "../cancel-trip-context";

interface Props {
  status: RefundFormState;
  purchase_cancellation?: NapiPurchaseCancellation;
}

export const CancellationStatusInfo: React.FC<Props> = ({
  status,
  purchase_cancellation
}) => {
  const { translator, locale } = useAppContext();
  const { closeModal } = useModalActions();
  const { purchase, selected_cancellation_option } = useCancelTripContext();
  const display_hepstar_banner = hasRefundProtectionPurchased(purchase.items);

  const onAction = (button_type: ErrorModalButtonType) =>
    button_type === "primary" || button_type === "secondary"
      ? closeModal()
      : getCtaAction(status, locale);

  const dialog_message = getMessage(
    status,
    purchase,
    translator,
    selected_cancellation_option,
    purchase_cancellation
  );

  const message = React.useMemo(
    () =>
      React.isValidElement(dialog_message) ? (
        dialog_message
      ) : (
        <Markdown
          className="alert-markdown"
          source={dialog_message || ""}
          Component="span"
        />
      ),
    [dialog_message]
  );

  return (
    <CancelDialog
      title={getTitle(status, translator)}
      title_icon={getIcon(status)}
      message={
        <Stack spacing={1} direction="column" alignItems="center">
          <Text size="lg" color="text.secondary.opaque" align="center">
            {message}
          </Text>
          {!!display_hepstar_banner && (
            <Box pt={2}>
              <HepstarBannerChip
                is_displayed={display_hepstar_banner}
                {...getHepstarBannerProps(status, purchase, translator)}
              />
            </Box>
          )}
        </Stack>
      }
      {...getButtons(status, translator)}
      actionHandler={onAction}
      onClose={closeModal}
    />
  );
};
